import { useState } from "react";
import { motion } from "framer-motion";
import {
  CalendarCheck,
  MapPin,
  Phone,
  Clock,
  Stethoscope,
  ShieldCheck,
  Heart,
  Award,
  Star,
  ChevronDown,
  Mail,
  ClipboardList,
  Headphones,
} from "lucide-react";
import DemoBackBar from "../components/DemoBackBar";
import { clinic } from "../data/demos";

const insurance = ["Cigna", "Bupa", "Allianz", "Clalit", "Maccabi", "AXA"];

const accreditations = [
  { label: "ISO 9001 Certified", icon: ShieldCheck },
  { label: "JCI Aligned Practice", icon: Award },
  { label: "10+ years in care", icon: Stethoscope },
  { label: "Multilingual team", icon: Headphones },
];

const howItWorks = [
  {
    step: "01",
    title: "Pick your service",
    body: "Tell us why you're booking — primary, follow-up, screening, or pediatrics.",
  },
  {
    step: "02",
    title: "Choose a time",
    body: "Same week slots, including evenings and Saturday mornings."
  },
  {
    step: "03",
    title: "Walk in, get cared for",
    body: "Real face time with your clinician. Notes and follow ups same day."
  },
];

const faqs = [
  {
    q: "Do I need a referral?",
    a: "Not for primary, family, or pediatric visits. Referrals are only needed for specialist diagnostics.",
  },
  {
    q: "How long is a typical visit?",
    a: "Most appointments take 25 to 40 minutes. We deliberately keep slots a little longer so you do not feel rushed."
  },
  {
    q: "Can I book for my family?",
    a: "Yes. You can add up to 4 family members to one account and book back to back appointments."
  },
  {
    q: "What insurance do you accept?",
    a: "We work with most major local and international insurers. Bring your card on the first visit and we will verify in real time."
  },
  {
    q: "Do you offer telehealth?",
    a: "Yes, secure video consultations are available for follow ups, prescription renewals, and quick check ins.",
  },
];

const stats = [
  { n: "12k+", l: "Patients served" },
  { n: "4.9★", l: "Patient rating" },
  { n: "<48h", l: "Avg. booking" },
  { n: "30 min", l: "Avg. visit" },
];

export default function ClinicDemo() {
  return (
    <div className="theme-clinic min-h-screen bg-care-bg text-care-ink overflow-x-hidden">
      <DemoBackBar accentColor="#1A6FE8" demoName="CarePoint · Clinic" />

      <header className="sticky top-[40px] z-40 backdrop-blur-md bg-white/85 border-b border-care-blue/10">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 py-3 sm:py-4 flex items-center justify-between gap-3">
          <a href="#top" className="flex items-center gap-2 sm:gap-3 min-w-0">
            <span className="inline-flex h-8 w-8 sm:h-9 sm:w-9 items-center justify-center rounded-lg bg-care-blue text-white flex-shrink-0">
              <Heart size={14} />
            </span>
            <span className="font-display text-base sm:text-xl font-bold tracking-tight text-care-ink truncate">
              CarePoint <span className="text-care-blue hidden sm:inline">Clinic</span>
            </span>
          </a>
          <nav className="hidden lg:flex items-center gap-8 text-[15px] font-semibold text-care-ink/75">
            <a href="#services" className="hover:text-care-blue transition-colors">Services</a>
            <a href="#how" className="hover:text-care-blue transition-colors">How it works</a>
            <a href="#doctors" className="hover:text-care-blue transition-colors">Doctors</a>
            <a href="#book" className="hover:text-care-blue transition-colors">Book</a>
            <a href="#testimonials" className="hover:text-care-blue transition-colors">Patients</a>
            <a href="#faq" className="hover:text-care-blue transition-colors">FAQ</a>
          </nav>
          <a
            href="#book"
            className="text-[10px] sm:text-xs uppercase tracking-[0.18em] sm:tracking-[0.2em] bg-care-blue text-white font-bold px-4 sm:px-5 py-2.5 sm:py-3 rounded-md hover:bg-care-ink transition-colors flex-shrink-0"
            data-testid="clinic-book-top"
          >
            Book
          </a>
        </div>
      </header>

      {/* Hero */}
      <section
        id="top"
        className="relative px-5 sm:px-8 pt-16 pb-16 sm:pt-24 md:pt-28 md:pb-24"
        data-testid="clinic-hero"
      >
        <div className="absolute -top-20 -right-20 h-96 w-96 rounded-full bg-care-blue/5 blur-3xl pointer-events-none" />
        <div className="absolute top-40 -left-20 h-96 w-96 rounded-full bg-care-mint/5 blur-3xl pointer-events-none" />
        <div className="mx-auto max-w-7xl grid gap-10 lg:gap-12 lg:grid-cols-12 items-center">
          <div className="lg:col-span-7">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-care-blue/10 text-care-blue text-[10px] sm:text-[11px] uppercase tracking-[0.22em] sm:tracking-[0.25em] font-semibold">
              <ShieldCheck size={12} /> Certified Primary Care · Est. 2014
            </span>
            <motion.h1
              initial={{ opacity: 0, y: 22 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="mt-6 font-display text-[40px] leading-[1.02] sm:text-6xl md:text-7xl font-black tracking-[-0.025em] text-care-ink"
            >
              Modern primary care, <br className="hidden sm:block" />
              <span className="text-care-blue">on your schedule.</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.15 }}
              className="mt-5 sm:mt-6 max-w-xl text-care-ink/70 text-base sm:text-lg leading-relaxed"
            >
              {clinic.about}
            </motion.p>
            <div className="mt-8 sm:mt-10 flex flex-col sm:flex-row sm:flex-wrap gap-3">
              <a
                href="#book"
                className="bg-care-blue text-white font-bold uppercase tracking-[0.2em] text-xs px-7 py-4 rounded-md hover:bg-care-ink transition-colors text-center"
                data-testid="clinic-cta-book"
              >
                Book an Appointment
              </a>
              <a
                href={`tel:${clinic.phone.replace(/\s/g, "")}`}
                className="border border-care-blue/30 text-care-blue font-bold uppercase tracking-[0.2em] text-xs px-7 py-4 rounded-md hover:bg-care-blue/5 transition-colors inline-flex items-center justify-center gap-2"
                data-testid="clinic-cta-call"
              >
                <Phone size={14} /> Call us
              </a>
            </div>
            <div className="mt-10 sm:mt-12 grid grid-cols-2 sm:grid-cols-4 gap-y-6 gap-x-4 sm:gap-x-8 max-w-2xl">
              {stats.map((s) => (
                <Stat key={s.l} n={s.n} l={s.l} />
              ))}
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="relative rounded-3xl overflow-hidden aspect-[4/5] shadow-2xl shadow-care-ink/10">
              <img src={clinic.hero} alt="Clinic" className="absolute inset-0 h-full w-full object-cover" />
              <div className="absolute bottom-4 left-4 right-4 bg-white rounded-xl p-4 shadow-2xl shadow-care-ink/15 flex items-center gap-3">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-care-mint/15 text-care-mint flex-shrink-0">
                  <CalendarCheck size={16} />
                </span>
                <div className="min-w-0">
                  <div className="text-xs text-care-ink/55">Next available</div>
                  <div className="text-sm font-bold text-care-ink truncate">Today · 4:30 PM</div>
                </div>
                <a
                  href="#book"
                  className="ml-auto text-[10px] uppercase tracking-[0.2em] bg-care-blue text-white font-bold px-3 py-2 rounded-md hover:bg-care-ink transition-colors flex-shrink-0"
                >
                  Take it
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Insurance strip */}
      <section className="px-5 sm:px-8 py-8 sm:py-10 bg-white border-y border-care-blue/10" data-testid="clinic-insurance">
        <div className="mx-auto max-w-7xl flex flex-col sm:flex-row sm:items-center gap-5">
          <span className="text-[10px] uppercase tracking-[0.3em] text-care-blue font-semibold flex-shrink-0">
            Insurance accepted
          </span>
          <div className="flex flex-wrap items-center gap-x-6 sm:gap-x-10 gap-y-3 text-care-ink/60 font-display font-bold text-base sm:text-lg">
            {insurance.map((i) => (
              <span key={i}>{i}</span>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="px-5 sm:px-8 py-20 md:py-24 bg-care-bg" data-testid="clinic-how">
        <div className="mx-auto max-w-7xl">
          <div className="text-[11px] uppercase tracking-[0.3em] text-care-blue">How it works</div>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl md:text-5xl font-black tracking-[-0.02em] leading-[1.05]">
            Three steps. No paperwork chaos.
          </h2>
          <div className="mt-12 grid gap-5 sm:gap-6 md:grid-cols-3">
            {howItWorks.map((s, i) => (
              <motion.div
                key={s.step}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="relative bg-white rounded-2xl border border-care-blue/10 p-7 hover:border-care-blue/40 hover:shadow-lg hover:shadow-care-blue/5 transition-all"
              >
                <div className="font-display text-5xl font-black text-care-blue/15">{s.step}</div>
                <h3 className="mt-3 font-display text-xl font-bold">{s.title}</h3>
                <p className="mt-2 text-sm text-care-ink/65 leading-relaxed">{s.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section id="services" className="px-5 sm:px-8 py-20 md:py-24 bg-white" data-testid="clinic-services">
        <div className="mx-auto max-w-7xl">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div>
              <div className="text-[11px] uppercase tracking-[0.3em] text-care-blue">Services</div>
              <h2 className="mt-3 font-display text-3xl sm:text-4xl md:text-5xl font-black tracking-[-0.02em] leading-[1.05]">
                Comprehensive care, all under one roof.
              </h2>
            </div>
            <a
              href="#book"
              className="text-xs uppercase tracking-[0.2em] text-care-blue font-bold hover:text-care-ink transition-colors flex items-center gap-1"
            >
              See all <ClipboardList size={14} />
            </a>
          </div>
          <div className="mt-10 sm:mt-12 grid gap-4 sm:gap-5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
            {clinic.services.map((s, i) => (
              <motion.div
                key={s.name}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                className="rounded-xl border border-care-blue/10 p-5 sm:p-6 bg-white hover:border-care-blue/40 hover:shadow-lg hover:shadow-care-blue/5 transition-all"
                data-testid={`clinic-service-${i}`}
              >
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-care-blue/10 text-care-blue">
                  <Stethoscope size={16} />
                </span>
                <h3 className="mt-4 font-display text-base sm:text-lg font-bold">{s.name}</h3>
                <p className="mt-2 text-sm text-care-ink/65 leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Accreditations */}
      <section className="px-5 sm:px-8 py-16 bg-care-bg" data-testid="clinic-accreditations">
        <div className="mx-auto max-w-7xl">
          <div className="text-[11px] uppercase tracking-[0.3em] text-care-blue text-center">Why CarePoint</div>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {accreditations.map((a) => {
              const Icon = a.icon;
              return (
                <div
                  key={a.label}
                  className="bg-white rounded-xl border border-care-blue/10 p-5 flex items-center gap-3"
                >
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-care-mint/15 text-care-mint flex-shrink-0">
                    <Icon size={16} />
                  </span>
                  <span className="text-sm font-semibold text-care-ink">{a.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Doctors */}
      <section id="doctors" className="px-5 sm:px-8 py-20 md:py-24 bg-white" data-testid="clinic-doctors">
        <div className="mx-auto max-w-7xl">
          <div className="text-[11px] uppercase tracking-[0.3em] text-care-blue">Our Team</div>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl md:text-5xl font-black tracking-[-0.02em] leading-[1.05]">
            Meet the clinicians.
          </h2>
          <div className="mt-10 sm:mt-12 grid gap-5 sm:gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
            {clinic.doctors.map((d, i) => (
              <motion.div
                key={d.name}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.07 }}
                className="group rounded-2xl bg-white border border-care-blue/10 overflow-hidden hover:shadow-xl hover:shadow-care-blue/10 transition-all hover-lift"
                data-testid={`clinic-doctor-${i}`}
              >
                <div className="aspect-[4/5] overflow-hidden">
                  <img
                    src={d.img}
                    alt={d.name}
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <div className="p-5">
                  <h3 className="font-display text-base sm:text-lg font-bold leading-tight">{d.name}</h3>
                  <p className="text-care-blue text-xs sm:text-sm font-semibold mt-1">{d.role}</p>
                  <p className="mt-3 text-xs text-care-ink/60 leading-relaxed">{d.bio}</p>
                  <div className="mt-4 flex gap-1 text-care-blue">
                    {Array.from({ length: 5 }).map((_, k) => (
                      <Star key={k} size={11} fill="currentColor" />
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Book */}
      <section id="book" className="px-5 sm:px-8 py-20 md:py-24 bg-care-ink text-white" data-testid="clinic-book">
        <div className="mx-auto max-w-7xl grid gap-10 lg:grid-cols-2">
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-care-mint">Book</div>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl md:text-5xl font-black tracking-[-0.02em] leading-[1.05]">
              Book an appointment in 60 seconds.
            </h2>
            <p className="mt-5 text-white/65 max-w-md text-sm sm:text-base">
              Submit the form and we'll confirm by phone or message within 30
              minutes during opening hours.
            </p>
            <ul className="mt-8 sm:mt-10 space-y-4 text-white/85 text-sm sm:text-base">
              <li className="flex items-center gap-3"><CalendarCheck size={16} className="text-care-mint flex-shrink-0" /> Online and in person visits</li>
              <li className="flex items-center gap-3"><ShieldCheck size={16} className="text-care-mint flex-shrink-0" /> Insurance friendly</li>
              <li className="flex items-center gap-3"><Clock size={16} className="text-care-mint flex-shrink-0" /> Same-week availability</li>
            </ul>
          </div>
          <BookingForm doctors={clinic.doctors} services={clinic.services} />
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="px-5 sm:px-8 py-20 md:py-24 bg-white" data-testid="clinic-testimonials">
        <div className="mx-auto max-w-7xl">
          <div className="text-[11px] uppercase tracking-[0.3em] text-care-blue">Patient Stories</div>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl md:text-5xl font-black tracking-[-0.02em] leading-[1.05]">
            Real care, real outcomes.
          </h2>
          <div className="mt-10 sm:mt-12 grid gap-5 sm:gap-6 md:grid-cols-3">
            {clinic.testimonials.map((t, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="rounded-2xl border border-care-blue/10 p-6 sm:p-7 hover:border-care-blue/40 transition-colors"
              >
                <div className="flex gap-1 text-care-blue">
                  {Array.from({ length: 5 }).map((_, k) => (
                    <Star key={k} size={12} fill="currentColor" />
                  ))}
                </div>
                <p className="mt-4 text-care-ink/85 leading-relaxed">"{t.quote}"</p>
                <p className="mt-5 text-xs uppercase tracking-[0.25em] text-care-ink/50">— {t.name}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="px-5 sm:px-8 py-20 md:py-24 bg-care-bg" data-testid="clinic-faq">
        <div className="mx-auto max-w-3xl">
          <div className="text-[11px] uppercase tracking-[0.3em] text-care-blue text-center">FAQ</div>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl md:text-5xl font-black tracking-[-0.02em] leading-[1.05] text-center">
            Quick answers.
          </h2>
          <div className="mt-10 sm:mt-12 divide-y divide-care-blue/10 border-y border-care-blue/10">
            {faqs.map((f, i) => (
              <FaqItem key={i} q={f.q} a={f.a} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* Visit */}
      <section id="visit" className="px-5 sm:px-8 py-20 md:py-24 bg-white" data-testid="clinic-visit">
        <div className="mx-auto max-w-7xl grid gap-10 md:grid-cols-2">
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-care-blue">Visit</div>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl md:text-5xl font-black tracking-[-0.02em] leading-[1.05]">
              Find the clinic.
            </h2>
            <ul className="mt-8 space-y-4 text-care-ink/85">
              <li className="flex items-center gap-3 text-sm sm:text-base"><MapPin size={16} className="text-care-blue flex-shrink-0" /> {clinic.address}</li>
              <li className="flex items-center gap-3 text-sm sm:text-base"><Phone size={16} className="text-care-blue flex-shrink-0" /> {clinic.phone}</li>
              <li className="flex items-center gap-3 text-sm sm:text-base"><Mail size={16} className="text-care-blue flex-shrink-0" /> hello@carepointclinic.com</li>
            </ul>
            <h3 className="mt-10 text-[11px] uppercase tracking-[0.3em] text-care-blue">Hours</h3>
            <ul className="mt-4 space-y-2 text-care-ink/75 font-mono-tech text-xs sm:text-sm">
              {clinic.hours.map((h) => (
                <li key={h.day} className="flex justify-between max-w-xs border-b border-care-blue/10 py-2">
                  <span>{h.day}</span><span>{h.time}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl overflow-hidden aspect-[4/3] md:aspect-auto md:min-h-[360px] border border-care-blue/10">
            <img
              src="https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=1200&q=80"
              alt="Clinic exterior"
              loading="lazy"
              decoding="async"
              className="h-full w-full object-cover"
            />
          </div>
        </div>
      </section>

      <footer className="border-t border-care-blue/10 px-5 sm:px-8 py-10 text-center text-xs text-care-ink/45">
        © {new Date().getFullYear()} CarePoint Clinic — Demo by Salama Solutions
      </footer>
    </div>
  );
}

function Stat({ n, l }) {
  return (
    <div>
      <div className="font-display text-2xl sm:text-3xl font-black text-care-blue">{n}</div>
      <div className="text-[10px] uppercase tracking-[0.22em] sm:tracking-[0.25em] text-care-ink/50 mt-1">{l}</div>
    </div>
  );
}

function FaqItem({ q, a, index }) {
  const [open, setOpen] = useState(index === 0);
  return (
    <div className="py-1">
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        className="w-full flex items-center justify-between gap-4 text-left py-5"
        data-testid={`clinic-faq-${index}`}
      >
        <span className="font-display text-base sm:text-lg font-bold text-care-ink">{q}</span>
        <ChevronDown
          size={18}
          className={`text-care-blue transition-transform duration-300 flex-shrink-0 ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>
      <motion.div
        initial={false}
        animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
        className="overflow-hidden"
      >
        <p className="pb-5 text-sm sm:text-base text-care-ink/70 leading-relaxed max-w-2xl">{a}</p>
      </motion.div>
    </div>
  );
}

function BookingForm({ doctors, services }) {
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    name: "",
    phone: "",
    service: services[0]?.name || "",
    doctor: doctors[0]?.name || "Any available",
    date: "",
    time: "",
  });
  const u = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  if (done) {
    return (
      <div className="rounded-2xl bg-white text-care-ink p-8 sm:p-10 text-center" data-testid="clinic-booking-success">
        <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-care-mint/15 text-care-mint">
          <CalendarCheck size={20} />
        </span>
        <h3 className="mt-5 font-display text-2xl font-bold">Appointment requested</h3>
        <p className="mt-3 text-care-ink/65 max-w-sm mx-auto text-sm">
          Thanks {form.name?.split(" ")[0]} — our front desk will confirm your
          slot ({form.date} {form.time}) shortly.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        setDone(true);
      }}
      className="rounded-2xl bg-white text-care-ink p-6 sm:p-8 shadow-2xl shadow-black/30"
      data-testid="clinic-booking-form"
    >
      <div className="grid gap-3 sm:gap-4">
        <input required placeholder="Full name" value={form.name} onChange={u("name")} className="clinic-input" data-testid="clinic-input-name" />
        <input required placeholder="Phone number" value={form.phone} onChange={u("phone")} className="clinic-input" data-testid="clinic-input-phone" />
        <select required value={form.service} onChange={u("service")} className="clinic-input" data-testid="clinic-select-service">
          {services.map((s) => <option key={s.name}>{s.name}</option>)}
        </select>
        <select value={form.doctor} onChange={u("doctor")} className="clinic-input" data-testid="clinic-select-doctor">
          <option>Any available</option>
          {doctors.map((d) => <option key={d.name}>{d.name}</option>)}
        </select>
        <div className="grid grid-cols-2 gap-3">
          <input required type="date" value={form.date} onChange={u("date")} className="clinic-input" data-testid="clinic-input-date" />
          <input required type="time" value={form.time} onChange={u("time")} className="clinic-input" data-testid="clinic-input-time" />
        </div>
        <button
          type="submit"
          className="mt-2 bg-care-blue text-white font-bold uppercase tracking-[0.2em] text-xs px-7 py-4 rounded-md hover:bg-care-ink transition-colors"
          data-testid="clinic-submit-booking"
        >
          Request Appointment
        </button>
      </div>
      <style>{`
        .clinic-input {
          width: 100%;
          background: #F5F8FB;
          border: 1px solid rgba(26, 111, 232, 0.15);
          color: #0E2A47;
          padding: 14px 16px;
          font-size: 14px;
          border-radius: 10px;
          outline: none;
        }
        .clinic-input:focus { border-color: #1A6FE8; background: #fff; }
      `}</style>
    </form>
  );
}
